/**
 * Photos from Willow's manufacturing unit and display studio (JP Nagar, Bangalore),
 * used in image slots across the site.
 *
 * File names: what is shown, long-tail, no city (see projectPhotos.ts for the rule).
 * Alt text: one factual sentence of what is visible; the locality is named on the
 * factory shots. Display-studio photos are described as the studio at our unit —
 * never as a client's home.
 */

export interface SitePhotoData {
  src: string;
  alt: string;
  width: number;
  height: number;
}

const m = (file: string) => `/assets/images/manufacturing/${file}`;

export const SITE_PHOTOS = {
  panelSaw: {
    src: m('panel-saw-for-cutting-boards-to-size-modular-furniture-unit.jpg'),
    alt: 'Panel saw for cutting boards to size at Willow’s modular furniture manufacturing unit in JP Nagar, Bangalore',
    width: 1086,
    height: 1448,
  },
  edgeBander: {
    src: m('edge-banding-machine-with-pre-milling-unit.jpg'),
    alt: 'Edge banding machine with a pre-milling unit that trims each panel edge clean before the band is applied, for a flush, durable finish',
    width: 1448,
    height: 1086,
  },
  coldPress: {
    src: m('cold-press-machine-for-laminate-pressing.jpg'),
    alt: 'Cold press machine used to bond laminate evenly onto boards at Willow’s manufacturing unit',
    width: 1086,
    height: 1448,
  },
  factoryFloor: {
    src: m('modular-furniture-manufacturing-unit-factory-floor.jpg'),
    alt: 'Factory floor of Willow’s modular furniture manufacturing unit in JP Nagar, Bangalore, with panels laid out and an edge banding line',
    width: 1448,
    height: 1086,
  },
  floorDisplayArea: {
    src: m('manufacturing-unit-floor-with-glass-walled-display-area-and-boards.jpg'),
    alt: 'Willow manufacturing unit floor with a glass-walled display area, board stock and a panel saw',
    width: 1448,
    height: 1086,
  },
  stabilizerCompressor: {
    src: m('voltage-stabilizer-and-air-compressor-at-manufacturing-unit.jpg'),
    alt: 'Voltage stabilizer and air compressor powering the machines at Willow’s manufacturing unit, with board stock on the factory floor',
    width: 1448,
    height: 1086,
  },
  hingeTest: {
    src: m('hinge-corrosion-test-hinges-in-water-filled-glass-jars.jpg'),
    alt: 'Hinge corrosion test at Willow — cabinet hinges kept in water-filled glass jars to check rust resistance and long-term durability',
    width: 1086,
    height: 1448,
  },
  team: {
    src: m('willow-team-reviewing-a-finished-panel-in-display-studio.jpg'),
    alt: 'Willow Interiors team reviewing a finished modular panel in the display studio at the manufacturing unit',
    width: 1087,
    height: 1447,
  },
  studioKitchen: {
    src: m('display-studio-grey-high-gloss-modular-kitchen-with-open-wooden-shelves.jpg'),
    alt: 'Grey high-gloss modular kitchen with open wooden shelves and profile lighting in Willow’s display studio',
    width: 1024,
    height: 1536,
  },
  studioLounge: {
    src: m('display-studio-lounge-with-stone-texture-wall-and-wood-panelling.jpg'),
    alt: 'Lounge in Willow’s display studio with a stone-texture feature wall, wood panelling and a cane sideboard',
    width: 1041,
    height: 1510,
  },
  studioBarCounter: {
    src: m('display-studio-bar-counter-with-curved-wooden-base-and-stools.jpg'),
    alt: 'Bar counter with a curved wooden base, stone top and black stools in Willow’s display studio',
    width: 1019,
    height: 1543,
  },
  studioCabinet: {
    src: m('display-studio-lit-fluted-glass-crockery-cabinet.jpg'),
    alt: 'Lit crockery cabinet with fluted glass shutters and open shelves in Willow’s display studio',
    width: 1024,
    height: 1536,
  },
  laminateArch: {
    src: m('marble-finish-laminate-arch-panel-with-material-sample-board.jpg'),
    alt: 'Marble-finish laminate arch panel beside a wood-grain wall, with a material sample board showing finish options',
    width: 1029,
    height: 1528,
  },
  drawerHardware: {
    src: m('pull-out-kitchen-drawer-with-plate-organiser-hardware.jpg'),
    alt: 'Pull-out kitchen drawer with a plate organiser and metal drawer system, in a grey high-gloss unit',
    width: 1254,
    height: 1254,
  },

  // ── Process, client visits and site delivery ──
  plywoodUnloading: {
    src: m('unloading-plywood-sheets-at-manufacturing-unit.jpg'),
    alt: 'Willow team unloading plywood sheets from a delivery truck at the manufacturing unit in JP Nagar, Bangalore',
    width: 1086,
    height: 1448,
  },
  glueApplication: {
    src: m('glue-application-on-plywood-before-laminate-pasting.jpg'),
    alt: 'Craftsman rolling an even coat of glue onto a plywood panel before laminate pasting, with the cold press behind',
    width: 1086,
    height: 1448,
  },
  clientVisit: {
    src: m('client-visiting-factory-to-watch-laminate-pasting-on-plywood-panels.jpg'),
    alt: 'Client visiting Willow’s factory to watch laminate being pasted onto plywood panels for their home',
    width: 1671,
    height: 941,
  },
  clientReview: {
    src: m('client-reviewing-laminate-pasting-with-willow-team-at-factory.jpg'),
    alt: 'Clients reviewing the laminate pasting process with the Willow team on the factory floor',
    width: 1672,
    height: 941,
  },
  packedLoading: {
    src: m('packed-modular-furniture-panels-loaded-for-onsite-assembly.jpg'),
    alt: 'Individually packed modular furniture panels being loaded for delivery and on-site assembly',
    width: 1086,
    height: 1448,
  },
  sitePanels: {
    src: m('modular-furniture-panels-delivered-to-site-ready-for-installation.jpg'),
    alt: 'Factory-finished modular furniture panels delivered to a client’s apartment, stacked and ready for installation',
    width: 1086,
    height: 1448,
  },
  boardCutting: {
    src: m('willow-team-cutting-boards-to-size-on-panel-saw-at-factory.jpg'),
    alt: 'Willow team cutting plywood boards to size on a sliding panel saw at the manufacturing unit in JP Nagar, Bangalore',
    width: 1536,
    height: 1024,
  },
  panelSawOperation: {
    src: m('panel-saw-in-operation-modular-furniture-manufacturing-unit.jpg'),
    alt: 'Panel saw in operation under the “The Future of Space Design” sign, with a supervisor checking cuts for modular furniture components',
    width: 1536,
    height: 1024,
  },
  teamDiscussion: {
    src: m('willow-team-planning-discussion-on-factory-floor.jpg'),
    alt: 'Willow team planning a project on the factory floor, beside the cold press and the material sample library',
    width: 1536,
    height: 1024,
  },
  adhesiveSpreading: {
    src: m('applying-adhesive-evenly-across-plywood-panel-before-laminate-pasting.jpg'),
    alt: 'Craftsman spreading adhesive evenly across a plywood panel before laminate pasting, with the panel saw behind, at Willow’s unit in JP Nagar, Bangalore',
    width: 1086,
    height: 1448,
  },
  truckLoading: {
    src: m('willow-team-loading-wrapped-panels-into-truck-for-site-delivery.jpg'),
    alt: 'Willow team loading individually wrapped furniture panels into a truck for delivery to the client’s site',
    width: 1086,
    height: 1448,
  },
  floorProtection: {
    src: m('floor-protection-sheet-laid-before-interior-installation.jpg'),
    alt: 'Willow team laying a protective sheet over the client’s floor before interior installation begins',
    width: 1448,
    height: 1086,
  },
} satisfies Record<string, SitePhotoData>;
